// İSG uzmanı portalı — Taşeron detayı: Saha erişimi sekmesi (api-contract §13.4).
// `GET /contractors/{uuid}/sites` liste + ekle (§10.3 `GET /sites`'ten seç → POST) +
// kaldır (soft-delete). Taşeron hangi sahalarda çalışabilir; saha adı/kodu tenant
// çekirdeğinden çözülür.

import {
  Button,
  ConfirmDialog,
  type ContractorSiteDto,
  EmptyState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  contractorsApi,
  currentCulture,
  resolveApiErrorMessage,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contractorsKeys } from '../../lib/contractorsKeys';
import { formatDate } from '../../lib/format';
import { TabPanelIntro } from '../TabPanelIntro';
import { SiteAccessModal } from './SiteAccessModal';

interface SitesPanelProps {
  contractorUuid: string;
}

export function SitesPanel({ contractorUuid }: SitesPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const culture = currentCulture();

  const [addOpen, setAddOpen] = useState(false);
  const [toRemove, setToRemove] = useState<ContractorSiteDto | null>(null);

  const query = useQuery({
    queryKey: contractorsKeys.sites(contractorUuid),
    queryFn: () => contractorsApi.listContractorSites(contractorUuid),
  });

  const removeMutation = useMutation({
    mutationFn: (siteUuid: string) => contractorsApi.removeContractorSite(contractorUuid, siteUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractorsKeys.sites(contractorUuid) });
      setToRemove(null);
    },
  });

  const rows = query.data ?? [];
  const assignedSiteUuids = useMemo(() => rows.map((s) => s.siteUuid), [rows]);

  return (
    <div className="flex flex-col gap-4">
      <TabPanelIntro
        action={
          <Button size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {t('contractors.sites.add.new')}
          </Button>
        }
      >
        {t('contractors.sites.subtitle')}
      </TabPanelIntro>

      <Table caption={t('contractors.sites.title')}>
        <TableHeader>
          <TableRow>
            <TableHead>{t('contractors.sites.col.site')}</TableHead>
            <TableHead>{t('contractors.sites.col.code')}</TableHead>
            <TableHead>{t('contractors.sites.col.since')}</TableHead>
            <TableHead className="text-right">{t('contractors.common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.isPending ? (
            <TableMessageRow colSpan={4}>
              <Spinner label={t('contractors.common.loading')} />
            </TableMessageRow>
          ) : query.isError ? (
            <TableMessageRow colSpan={4}>{resolveApiErrorMessage(query.error, t)}</TableMessageRow>
          ) : rows.length === 0 ? (
            <TableMessageRow colSpan={4}>
              <EmptyState icon={MapPin} title={t('contractors.sites.empty')} />
            </TableMessageRow>
          ) : (
            rows.map((site) => (
              <TableRow key={site.uuid}>
                <TableCell className="font-medium text-fg">{site.siteName}</TableCell>
                <TableCell className="text-muted">{site.siteCode ?? '—'}</TableCell>
                <TableCell className="whitespace-nowrap text-muted">
                  {formatDate(site.createdAt, culture)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setToRemove(site)}
                      aria-label={t('contractors.sites.action.remove')}
                    >
                      <Trash2 className="h-4 w-4 text-danger" aria-hidden="true" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <SiteAccessModal
        open={addOpen}
        onOpenChange={setAddOpen}
        contractorUuid={contractorUuid}
        assignedSiteUuids={assignedSiteUuids}
      />
      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title={t('contractors.sites.remove.title')}
        body={t('contractors.sites.remove.body', { site: toRemove?.siteName ?? '' })}
        confirmLabel={t('contractors.sites.remove.confirm')}
        loading={removeMutation.isPending}
        error={removeMutation.isError ? removeMutation.error : undefined}
        onConfirm={() => toRemove && removeMutation.mutate(toRemove.siteUuid)}
      />
    </div>
  );
}
